import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useUEat } from '@/contexts/UEatContext';
import { useOrdering } from '@/contexts/OrderingContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Clock, MapPin, UtensilsCrossed, Gift, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = '/assets/images/logos/xy43zlm2_logo-belgo-831e91aa.avif';
const TRAITEUR_LOGO = '/assets/images/site/whkmq2k8_image-5e70356b.png';
const CAMION_LOGO = '/assets/images/logos/jv8nh47j_logo-camion-belgo-c7d04f11.avif';

export default function HomePage() {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const ueat = useUEat();
  const ordering = useOrdering();
  const [featured, setFeatured] = useState([]);
  const [holidayActive, setHolidayActive] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/menu/available`).then(r => {
      setFeatured(r.data.filter(item => item.featured).slice(0, 4));
    }).catch(() => {});
    axios.get(`${API}/holiday-menu`).then(r => {
      if (r.data && r.data.active) setHolidayActive(true);
    }).catch(() => {});
    axios.get(`${API}/site-content/home`).then(r => setContent(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: get a localized field with fallback to translation key
  const pick = (obj, frKey, enKey, fallback = '') => {
    if (!obj) return fallback;
    return (lang === 'fr' ? obj[frKey] : obj[enKey]) || fallback;
  };

  const hero = content?.hero || {};
  const ctaBar = content?.cta_bar || {};
  const services = content?.services || [];
  const aboutPreview = content?.about_preview || {};
  const quickInfo = content?.quick_info || {};

  return (
    <div data-testid="home-page">
      {/* Floating Holiday Menu Button */}
      {holidayActive && (
        <Link
          to="/holiday-menu"
          className="fixed left-4 bottom-20 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-[#e53a3a] text-white font-bold shadow-xl hover:bg-[#c62828] transition-all hover:scale-105 animate-bounce"
          style={{ animationDuration: '2s', animationIterationCount: '3' }}
          data-testid="holiday-menu-floating-btn"
        >
          <Gift className="w-5 h-5" />
          <span className="hidden sm:inline text-sm">{lang === 'fr' ? 'Menu des Fêtes' : 'Holiday Menu'}</span>
        </Link>
      )}

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center bg-black" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={hero.background_image || '/assets/images/fallback/photo-1647482770207-4e8f5ba7b33e-b1b0e82a.jpg'}
            alt="Hero"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <Badge className="mb-4 text-xs tracking-[0.2em] uppercase font-bold px-3 py-1 bg-[#e53a3a] text-white border-0">
                {pick(hero, 'badge_fr', 'badge_en', 'Saint-Jean-sur-Richelieu')}
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#f4d00d] leading-tight tracking-tight mb-4">
                {pick(hero, 'title_main_fr', 'title_main_en', 'Resto')} <span className="text-[#f4d00d]">{pick(hero, 'title_highlight_fr', 'title_highlight_en', 'Belgo')}</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-200 font-body mb-2">
                {pick(hero, 'subtitle_fr', 'subtitle_en', t('hero.subtitle'))}
              </p>
              <p className="text-base text-gray-400 font-body mb-8 max-w-lg">
                {pick(hero, 'tagline_fr', 'tagline_en', t('hero.tagline'))}
              </p>
              <div className="flex flex-wrap gap-4">
                  <a href="https://www.restobelgo.com/assets/files/pdf/Belgo_menu_noel_2026.pdf" target="_blank" rel="noopener noreferrer" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-bold bg-[#e53a3a] hover:bg-[#c62828] transition-colors" data-testid="hero-view-menu-ueat">
                    MENU TRAITEUR DES FÊTES <ExternalLink className="w-4 h-4" />
                  </a>
                {ueat.enabled && ueat.url ? (
                  <a href={ueat.url} target="_blank" rel="noopener noreferrer" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-[#f4d00d] text-black hover:bg-[#d4b800] transition-colors" data-testid="hero-order-ueat">
                    {pick(hero, 'cta_order_fr', 'cta_order_en', lang === 'fr' ? 'POUR EMPORTER' : 'FOR TAKEOUT')} <ExternalLink className="w-4 h-4" />
                  </a>
                ) : (
                  <Link to="/menu" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-[#e53a3a] text-white hover:bg-[#c62828] transition-colors" data-testid="hero-order">
                    {pick(hero, 'cta_order_fr', 'cta_order_en', lang === 'fr' ? 'POUR EMPORTER' : 'FOR TAKEOUT')}
                  </Link>
                )}
                <a  href={hero.delivery_url || 'https://www.skipthedishes.com/belgio-resto-et-traiteur'} target="_blank" rel="noopener noreferrer" className="btn-belgo bg-[#f4d00d] hover:bg-[#d4b800] inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-black hover:bg-[#d4b800] hover:text-black transition-colors" data-testid="hero-delivery">
                  {pick(hero, 'cta_delivery_fr', 'cta_delivery_en', 'Commande pour livraison')}
                </a>
<a href="https://www.doordash.com/fr-CA/store/resto-belgo-saint-jean-sur-richelieu-51947239/121181758/?cursor=eyJzZWFyY2hfaXRlbV9jYXJvdXNlbF9jdXJzb3IiOnsicXVlcnkiOiJyZXN0byBiZWxnbyIsIml0ZW1faWRzIjpbXSwic2VhcmNoX3Rlcm0iOiJyZXN0byBiZWxsbyIsInZlcnRpY2FsX2lkIjotOTk5LCJ2ZXJ0aWNhbF9uYW1lIjoiYWxsIiwicXVlcnlfaW50ZW50IjoiU1RPUkVfUlgifSwic3RvcmVfcHJpbWFyeV92ZXJ0aWNhbF9pZHMiOlsxLDExMDAzNywxMTAwNDUsMTEwMDUyLDExMDA1NSwxMTAwNjIsMTEwMDgyLDExMDA4MywxOTIsNF19&pickup=false" target="_blank" rel="noopener noreferrer" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border-white bg-[#f4d00d] hover:bg-[#d4b800]  text-black hover:text-black transition-colors" data-testid="hero-delivery">
                  LIVRAISON AVEC DOORDASH
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:flex justify-center">
              <img src={LOGO_URL} alt="Resto Belgo" className="h-64 xl:h-80 w-auto object-contain drop-shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Buttons Bar */}
      <section className="bg-black py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-4">
          <a href={ctaBar.foodtruck_url || '/assets/files/pdf/22z8rqf9_Belgo_menu-foodtruck_2026-80234699.pdf'} target="_blank" rel="noopener noreferrer" className="btn-belgo px-6 py-3 rounded-lg font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors" data-testid="cta-foodtruck">
            {pick(ctaBar, 'foodtruck_fr', 'foodtruck_en', 'Réserver notre foodtruck')}
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-32 bg-[#f5f5f0]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const title = lang === 'fr' ? service.title_fr : service.title_en;
              const desc = lang === 'fr' ? service.desc_fr : service.desc_en;
              const inner = (
                <>
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <img src={service.image} alt={title} className="w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-70 transition-all duration-500" />
                    {service.logo && (
                      <div className="absolute inset-0 flex items-center justify-center p-4">
                        <img
                          src={service.logo}
                          alt={`${title} logo`}
                          className="max-h-32 sm:max-h-36 w-auto object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)] group-hover:scale-110 transition-transform duration-500"
                          data-testid={`service-logo-${idx}`}
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-2" data-testid={`service-title-${idx}`}>{title}</h3>
                    <p className="text-sm text-[#555555] mb-4">{desc}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#e53a3a]">
                      {service.external ? (lang === 'fr' ? 'En savoir plus' : 'Learn more') : (lang === 'fr' ? 'Découvrir' : 'Discover')} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </>
              );
              return (
                <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15, duration: 0.5 }}>
                  {service.external ? (
                    <a href={service.link} target="_blank" rel="noopener noreferrer" className="group block card-hover bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-200 overflow-hidden" data-testid={`service-card-${idx}`}>{inner}</a>
                  ) : (
                    <Link to={service.link || '/menu'} className="group block card-hover bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-200 overflow-hidden" data-testid={`service-card-${idx}`}>{inner}</Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      {featured.length > 0 && (
        <section className="py-20 bg-white" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1a1a1a] tracking-tight">
                {lang === 'fr' ? 'Nos Vedettes' : 'Our Favorites'}
              </h2>
              <div className="w-16 h-1 mx-auto mt-4 bg-[#e53a3a]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((item, i) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card-hover bg-[#f5f5f0] rounded-xl border border-gray-200 overflow-hidden" data-testid={`featured-item-${i}`}>
                  <div className="aspect-square overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={lang === 'fr' ? item.name_fr : item.name_en} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#e0e0e0]" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-heading text-lg font-semibold text-[#1a1a1a]">{lang === 'fr' ? item.name_fr : item.name_en}</h4>
                      <Star className="w-4 h-4 text-[#f4d00d] fill-[#f4d00d]" />
                    </div>
                    <p className="text-xs text-[#555555] mb-3 line-clamp-2">{lang === 'fr' ? item.description_fr : item.description_en}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[#e53a3a]">{item.price.toFixed(2)} $</span>
                      <button onClick={() => addItem(item)} className="btn-belgo px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#e53a3a] hover:bg-[#c62828]" data-testid={`add-featured-${i}`}>
                        {t('menu.addToCart')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10">
              {ueat.enabled && ueat.url && ueat.redirect_menu_link ? (
                <a href={ueat.url} target="_blank" rel="noopener noreferrer" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors" data-testid="see-full-menu-ueat">
                  {pick(hero, 'cta_menu_fr', 'cta_menu_en', t('hero.cta'))} <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <Link to="/menu" className="btn-belgo inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-black bg-[#f4d00d] hover:bg-[#d4b800] transition-colors" data-testid="see-full-menu">
                  {pick(hero, 'cta_menu_fr', 'cta_menu_en', t('hero.cta'))} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* About Preview */}
      <section className="py-20 lg:py-32 bg-[#f5f5f0]" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1a1a1a] tracking-tight mb-6">
                {pick(aboutPreview, 'title_fr', 'title_en', t('about.title'))}
              </h2>
              <p className="text-[#555555] leading-relaxed mb-4">{pick(aboutPreview, 'p1_fr', 'p1_en', t('about.p1'))}</p>
              <p className="text-[#555555] leading-relaxed mb-6">{pick(aboutPreview, 'p2_fr', 'p2_en', t('about.p2'))}</p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#e53a3a] font-semibold hover:underline" data-testid="learn-more-link">
                {lang === 'fr' ? 'En savoir plus' : 'Learn more'} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-xl overflow-hidden shadow-lg">
              <img src={aboutPreview.image || '/assets/images/fallback/pexels-photo-35505245-1b7caf19.jpeg'} alt="About" className="w-full h-80 lg:h-96 object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hours & Location */}
      <section className="py-16 bg-black text-white" data-testid="quick-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center gap-3">
              <Clock className="w-8 h-8 text-[#f4d00d]" />
              <h3 className="font-heading text-xl font-semibold">{t('contact.hours')}</h3>
              <p className="text-sm text-gray-400">{pick(quickInfo, 'hours_text_fr', 'hours_text_en', 'Mer-Dim: 11h - 20h/22h')}</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <MapPin className="w-8 h-8 text-[#e53a3a]" />
              <h3 className="font-heading text-xl font-semibold">{lang === 'fr' ? 'Nous Trouver' : 'Find Us'}</h3>
              <p className="text-sm text-gray-400">{quickInfo.address_text || '153 Champlain, Saint-Jean-sur-Richelieu'}</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <UtensilsCrossed className="w-8 h-8 text-[#f4d00d]" />
              <h3 className="font-heading text-xl font-semibold">{lang === 'fr' ? 'Commander' : 'Order'}</h3>
              <Link to="/menu" className="text-sm text-[#e53a3a] hover:underline">
                {t('nav.order')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
